/**
 * Every date this application draws, and every calendar day it turns into a filter.
 *
 * ⚠️ THE DEFECT THIS EXISTS FOR. Timestamps are stored and transported as UTC correctly —
 * `AppDbContext.ApplyUtcKindToTimestamps` guarantees the trailing `Z` and `TimestampKindTests`
 * holds it — and were then rendered with a bare `toLocaleString()`, which converts to the
 * BROWSER'S zone. The server resolves a different, authoritative answer for the same person
 * (`TimeZones.ResolveAsync`: their override, then their site's, then the tenant default) and
 * uses it to cut the day buckets on analytics, reports and the follow-up list.
 *
 * So one screen carried two definitions of "the reader's timezone" and stated the one it was
 * not using: `/todos` printed "Dates shown in America/Los_Angeles" above rows whose clock came
 * from a laptop in New York. The personal timezone control was inert for the same reason —
 * `timeZoneId` reached the browser and nothing but the picker's own label ever read it.
 *
 * ⚠️ It also reached the FILTERS. A calendar day is only a pair of instants once a zone is
 * known, and the queue's date range and the audit log's both anchored to browser midnight: a
 * reader at a New York site on a Pacific laptop asking for "raised on the 15th" was sent
 * `[15th 07:00Z, 16th 07:00Z)` and silently lost the day's first three Eastern hours while
 * silently gaining three of the next day's. The window was the right shape around the wrong
 * midnight, and nothing on screen said so.
 *
 * Everything here therefore takes the zone as an argument. There is no ambient default and no
 * overload without one, so a call site cannot fall back to the browser by omission — which is
 * the failure this module is replacing.
 *
 * ⚠️ NOT the SLA clock. Deadlines are computed server-side in the TICKET'S site zone
 * (D-72), never the reader's, so nothing here can move a commitment. This is presentation and
 * filter-boundary arithmetic only.
 */

/**
 * Which zone the host is in, for the one case with no session to read: a signed-out screen.
 *
 * ⚠️ Not a default for anything else. It is the browser's guess, and preferring it over the
 * server's resolved answer is the whole defect above.
 */
export function browserZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * A zone's offset from UTC, in milliseconds, AT A GIVEN INSTANT.
 *
 * ⚠️ At an instant, because an offset is not a property of a zone. `America/New_York` is
 * -5h in January and -4h in July, and a helper that answered "the offset of New York" would be
 * wrong for half the year — which is the bug this whole module is built to avoid rather than
 * relocate.
 *
 * Works by asking `Intl` to write the instant out in the target zone, reading the wall-clock
 * fields back, and treating them as if they had been UTC. The difference between that and the
 * real instant is the offset. `en-CA` and `hour12: false` are chosen because their parts are
 * unambiguous; nothing here is shown to anybody.
 */
export function zoneOffsetMs(instant: Date, zone: string): number {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);

  const field = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");

  // ⚠️ `hour12: false` renders midnight as hour 24 in some engines rather than 0, which would
  // put the offset out by a day exactly once per day. Normalised rather than trusted.
  const hour = field("hour") % 24;

  const asIfUtc = Date.UTC(
    field("year"),
    field("month") - 1,
    field("day"),
    hour,
    field("minute"),
    field("second"),
  );

  // Whole seconds: sub-second offsets do not exist, and the milliseconds are not in `parts`.
  return asIfUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/**
 * A wall-clock reading in a zone, as the instant it names.
 *
 * ⚠️ Iterated, and the iteration is what makes DST work. The offset can only be looked up for
 * an instant, but the instant is what we are solving for — so the first pass guesses using the
 * offset in force at the same clock reading treated as UTC, and the second re-asks at the
 * candidate. On the two days a year the offset changes, those disagree, and the first answer
 * is wrong by an hour. A single-pass version is right 363 days a year, which is precisely long
 * enough for nobody to notice it shipped.
 *
 * ⚠️ A candidate is only accepted when the offset used to build it is the offset actually in
 * force AT it. That self-consistency check is what distinguishes the two awkward cases from
 * each other, and both have a defined answer here:
 *
 * - The hour repeated each autumn, where a reading happens twice: the FIRST of the two, which
 *   is the candidate that validates.
 * - The hour skipped each spring, where a reading never happens and neither candidate
 *   validates: resolved FORWARD by the size of the gap, so 02:30 on a spring-forward Sunday
 *   is 03:30. This is the behaviour `Temporal` calls "compatible" and the one every date
 *   library converged on, and it matters beyond tidiness — resolving BACKWARD would put a
 *   midnight-transitioning zone's start-of-day on the PREVIOUS DAY, so a date filter would
 *   silently return a day nobody asked for. None of the offered US zones transition at
 *   midnight; the filter must not depend on that staying true.
 */
export function instantFromWallClock(
  zone: string,
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
): Date {
  // `Date.UTC` normalises out-of-range parts, which is what makes "hour + 3" past 21:00 and
  // "day + 1" on the 31st roll into the next day and month rather than producing nonsense.
  const asIfUtc = Date.UTC(year, month - 1, day, hour, minute, 0, 0);

  const first = zoneOffsetMs(new Date(asIfUtc), zone);
  const candidate = asIfUtc - first;
  if (zoneOffsetMs(new Date(candidate), zone) === first) return new Date(candidate);

  const second = zoneOffsetMs(new Date(candidate), zone);
  const alternative = asIfUtc - second;
  if (zoneOffsetMs(new Date(alternative), zone) === second) return new Date(alternative);

  // Neither validates: the reading is inside the skipped hour. Clocks springing forward means
  // the offset INCREASES, so the smaller of the two is the one in force before the change —
  // and subtracting it shifts the result past the gap rather than back behind it.
  return new Date(asIfUtc - Math.min(first, second));
}

/**
 * An instant's wall-clock fields in a zone — the parts a person would read off a clock there.
 *
 * `weekday` is 0 for Sunday, matching `Date.prototype.getDay`, so a caller reasoning about
 * "next Monday" does not also have to reason about which convention this returned.
 */
export function zonedParts(
  instant: Date,
  zone: string,
): { year: number; month: number; day: number; hour: number; minute: number; weekday: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    hour12: false,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(instant);

  const field = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");

  const names = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const weekday = names.indexOf(
    (parts.find((p) => p.type === "weekday")?.value ?? "").slice(0, 3).toLowerCase(),
  );

  return {
    year: field("year"),
    month: field("month"),
    day: field("day"),
    // See zoneOffsetMs: `hour12: false` can render midnight as 24.
    hour: field("hour") % 24,
    minute: field("minute"),
    weekday: weekday === -1 ? 0 : weekday,
  };
}

/**
 * A UTC instant as the value a `datetime-local` input wants, in a given zone.
 *
 * ⚠️ Built field by field rather than by slicing an ISO string. `toISOString().slice(0, 16)`
 * is the obvious version and it is wrong everywhere outside UTC — it shows the reader the
 * instant in Greenwich and then saves whatever they leave in the box as though it were local,
 * which shifts every date they open and re-save.
 */
export function toZonedInputValue(utc: string | null, zone: string): string {
  if (!utc) return "";

  const at = new Date(utc);
  if (Number.isNaN(at.getTime())) return "";

  const p = zonedParts(at, zone);
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * The inverse: what somebody typed into the box, read as a clock reading IN THAT ZONE.
 *
 * An empty box means no date, which is a value rather than a failure.
 *
 * ⚠️ Not `new Date(value)`, which reads an unzoned string as the BROWSER'S local time. That
 * round-trips correctly against a browser-zone renderer and silently disagrees with everything
 * the server bucketed — somebody at an Eastern site on a Pacific laptop set a reminder for
 * "09:00" and had it fall due at noon, under a heading that said 9.
 */
export function fromZonedInputValue(value: string, zone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;

  const [, year, month, day, hour, minute] = match.map(Number);

  return instantFromWallClock(zone, year, month, day, hour, minute).toISOString();
}

/**
 * A `YYYY-MM-DD` from a date input, split into numbers, or undefined if it is not one.
 *
 * ⚠️ Rejects `2026-02-31` and friends rather than letting them roll over. The `Date`
 * constructor silently turns that into 3 March, and a filter bound three days from what
 * somebody typed is worse than no filter — it looks like it worked.
 */
function parseDay(day: string | undefined): [number, number, number] | undefined {
  if (!day) return undefined;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return undefined;

  const [, year, month, date] = match.map(Number);

  // Calendar-real, checked before any zone is involved: this is arithmetic about the
  // Gregorian calendar, which no timezone changes.
  if (month < 1 || month > 12 || date < 1) return undefined;
  if (date > new Date(Date.UTC(year, month, 0)).getUTCDate()) return undefined;

  return [year, month, date];
}

/**
 * The instant a calendar day BEGINS in a zone, as an ISO string. Inclusive lower bound.
 *
 * Returns undefined for an empty or unparseable value, so a half-typed date narrows nothing
 * rather than filtering to an accidental instant.
 */
export function startOfDayUtc(day: string | undefined, zone: string): string | undefined {
  const parsed = parseDay(day);
  if (!parsed) return undefined;

  return instantFromWallClock(zone, parsed[0], parsed[1], parsed[2]).toISOString();
}

/**
 * The same day's EXCLUSIVE upper bound: the instant the FOLLOWING day begins in that zone.
 *
 * ⚠️ Exclusive on purpose. An inclusive bound has to be written as the last instant of a day,
 * and every version of that is wrong by some fraction of a second — a request raised at
 * 23:59:59.7 would be dropped from a range that names its own day, which reads as a filter
 * losing rows at random.
 *
 * ⚠️ Computed by advancing the DAY and re-resolving, never by adding 24 hours to the lower
 * bound. The day a zone springs forward is 23 hours long and the day it falls back is 25, so
 * a fixed 24 would clip an hour off one and include an hour of the next day on the other.
 */
export function endOfDayExclusiveUtc(day: string | undefined, zone: string): string | undefined {
  const parsed = parseDay(day);
  if (!parsed) return undefined;

  const [year, month, date] = parsed;

  // `Date.UTC` rolls the 31st into the 1st for us, which is what makes month ends work. Done
  // on a UTC calendar because it is pure date arithmetic — no instant is being named yet.
  const next = new Date(Date.UTC(year, month - 1, date + 1));

  return instantFromWallClock(
    zone,
    next.getUTCFullYear(),
    next.getUTCMonth() + 1,
    next.getUTCDate(),
  ).toISOString();
}

/**
 * Which calendar day an instant falls on in a zone, as `YYYY-MM-DD`.
 *
 * The inverse of {@link startOfDayUtc}, for pre-filling a date input from a range that came
 * back from the server.
 */
export function dayInZone(value: string | Date, zone: string): string | undefined {
  const at = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(at.getTime())) return undefined;

  // `en-CA` formats as `YYYY-MM-DD`, which is the shape a date input takes.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

// ---------------------------------------------------------------------------
// Rendering.
//
// Every function below takes the zone and passes it to Intl. A bare toLocale*String on a
// Date is banned by ESLint and by a CI guard, because this module is only a fix for as long
// as nothing goes around it.
// ---------------------------------------------------------------------------

/** Renders an instant in a zone, or an em dash when there is nothing to render. */
export function formatInstant(
  value: string | Date | null | undefined,
  zone: string,
  options: Intl.DateTimeFormatOptions,
): string {
  if (value === null || value === undefined || value === "") return "—";

  const at = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(at.getTime())) return "—";

  return new Intl.DateTimeFormat(undefined, { ...options, timeZone: zone }).format(at);
}

/** "15 Aug 2026, 19:24" — the default for a timestamp somebody may need to act on. */
export function formatDateTime(value: string | Date | null | undefined, zone: string): string {
  return formatInstant(value, zone, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "15 Aug, 19:24" — the same without the year, where the context already implies it. */
export function formatShortDateTime(value: string | Date | null | undefined, zone: string): string {
  return formatInstant(value, zone, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "15 Aug 2026" — for a deadline or a date somebody picked, where the time says nothing. */
export function formatDate(value: string | Date | null | undefined, zone: string): string {
  return formatInstant(value, zone, { year: "numeric", month: "short", day: "numeric" });
}

/** "19:24" — for a row already grouped under its own date. */
export function formatTime(value: string | Date | null | undefined, zone: string): string {
  return formatInstant(value, zone, { hour: "numeric", minute: "2-digit" });
}

/** "Mon 17 Aug" — a weekday-led date, for a due date being scanned down a list. */
export function formatWeekdayDate(value: string | Date | null | undefined, zone: string): string {
  return formatInstant(value, zone, { weekday: "short", day: "numeric", month: "short" });
}

/**
 * A `YYYY-MM-DD` a person picked, written out for a filter chip: "15 Aug 2026".
 *
 * ⚠️ Takes NO zone, deliberately, and it is the one function here that does not. The value is
 * already a calendar day — the one in the input box beside the chip — so there is no instant
 * to convert and no zone that could be the right one to convert it in. What varies is the
 * reader's locale, which `Intl` takes from the host.
 *
 * ⚠️ Pinned to UTC and read at noon so the formatter cannot roll it to a neighbouring date.
 * `new Date("2026-08-12")` is UTC midnight, so west of Greenwich the obvious version prints
 * the 11th — a chip naming a different day from the filter it describes.
 */
export function formatDayLabel(day: string | undefined): string {
  const parsed = parseDay(day);
  if (!parsed) return "—";

  const at = new Date(Date.UTC(parsed[0], parsed[1] - 1, parsed[2], 12));

  return new Intl.DateTimeFormat(undefined, {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(at);
}

/**
 * A CALENDAR DATE that is not an instant — a hire date — written out for display.
 *
 * ⚠️ THE ONE THING ON A SCREEN THAT MUST NOT BE CONVERTED, and the reason this exists rather
 * than an ESLint suppression on the call site. Somebody started work on a day, not at an
 * instant; there is no zone in which that day is a different day. Running it through
 * `formatDate` would subtract the reader's offset and print the day before for everybody west
 * of UTC — which is exactly the failure `AppDbContext.ApplyUtcKindToTimestamps` refuses to
 * create by leaving `EmployeeHireDate` out of the Utc converter, and it would be undone here.
 *
 * ⚠️ Takes the DATE PART OF THE STRING and never parses it as an instant. The server sends
 * `1998-06-15T00:00:00` with no `Z`, which `new Date` reads as local midnight — right by
 * accident in the browser's own zone and wrong the moment any zone is applied to it. Slicing
 * the day out removes the accident.
 */
export function formatCalendarDate(value: string | null | undefined): string {
  if (!value) return "—";

  return formatDayLabel(value.slice(0, 10));
}

/**
 * How a due date reads on a row: "Overdue — Mon 17 Aug, 10:00", "Today, 10:00", "Mon 17 Aug".
 *
 * Relative for the two days that matter and absolute after that. "In 9 days" is a number
 * somebody has to convert back into a date before they can act on it.
 *
 * ⚠️ "Today" is a calendar day IN THE READER'S ZONE, and it must agree with the server, which
 * cuts the same buckets with `TimeZones.ResolveAsync` and sends the zone it used. A desk
 * covering ~29 sites has people in more than one, and a second definition here would put an
 * item under a heading that disagreed with its own row.
 */
export function describeDue(dueAtUtc: string | null, zone: string, now = new Date()): string {
  if (!dueAtUtc) return "No date";

  const at = new Date(dueAtUtc);
  if (Number.isNaN(at.getTime())) return "No date";

  const time = formatTime(at, zone);
  const days = calendarDaysBetween(now, at, zone);

  if (at.getTime() <= now.getTime()) return `Overdue — ${formatWeekdayDate(at, zone)}, ${time}`;
  if (days === 0) return `Today, ${time}`;
  if (days === 1) return `Tomorrow, ${time}`;

  return `${formatWeekdayDate(at, zone)}, ${time}`;
}

/**
 * Whole calendar days between two instants, counted in a zone.
 *
 * ⚠️ Compared on the DATE, not on elapsed hours. "Tomorrow" at 23 hours' distance and at 2
 * hours' distance are both tomorrow, and dividing a millisecond difference by 86,400,000
 * calls the first one "today".
 *
 * ⚠️ And counted by converting each day back to ITS OWN midnight in the zone rather than by
 * subtracting the two instants — the days either side of a DST change are not 24 hours long,
 * so the subtraction is off by an hour twice a year and rounds to the wrong day whenever the
 * two readings sit near midnight.
 */
export function calendarDaysBetween(from: Date, to: Date, zone: string): number {
  const fromDay = dayInZone(from, zone);
  const toDay = dayInZone(to, zone);
  if (!fromDay || !toDay) return 0;

  const a = parseDay(fromDay);
  const b = parseDay(toDay);
  if (!a || !b) return 0;

  const asUtc = (d: [number, number, number]) => Date.UTC(d[0], d[1] - 1, d[2]);

  return Math.round((asUtc(b) - asUtc(a)) / 86_400_000);
}
